/**
 * Blog Post Service Implementation
 * 
 * Implements IBlogPostService with clean architecture principles,
 * proper error handling, and dependency injection.
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */

import type { BlogPost } from '../../../types';
import type { 
  IBlogPostService, 
  ServiceResult, 
  PaginationParams, 
  PaginatedResult 
} from '../interfaces/IDataService';
import type { ILogger } from '../logging/Logger';
import type { ICacheService } from '../interfaces/IDataService';
import { publicAPI } from '../../../lib/api';
import { AppError, ValidationError } from '../../../utils/errorHandler';
import { BlogPostAdapter } from '../../adapters/BlogPostAdapter';

/**
 * Blog Post Service Implementation
 */
export class BlogPostService implements IBlogPostService {
  private readonly logger: ILogger;
  private readonly cache?: ICacheService;
  private readonly cachePrefix = 'blog:';
  private readonly cacheTTL = 300000; // 5 minutes

  constructor(logger: ILogger, cache?: ICacheService) {
    this.logger = logger.child('BlogPostService');
    this.cache = cache;
  }

  /**
   * Get all blog posts with pagination
   */
  public async getAll(params?: Partial<PaginationParams>): Promise<ServiceResult<PaginatedResult<BlogPost>>> {
    const context = 'getAll';
    this.logger.debug('Fetching all blog posts', context, { params });

    try {
      const cacheKey = this.getCacheKey('all', params);
      
      // Try cache first
      if (this.cache) {
        const cached = await this.cache.get<PaginatedResult<BlogPost>>(cacheKey);
        if (cached.success && cached.data) {
          this.logger.debug('Returning cached blog posts', context);
          return cached;
        }
      }

      // Fetch from API
      const posts = await publicAPI.getPublishedPosts();
      const domainPosts = BlogPostAdapter.toDomainArray(posts);
      const result = this.paginateResults(domainPosts, params);

      // Cache the result
      if (this.cache) {
        await this.cache.set(cacheKey, result, this.cacheTTL);
      }

      this.logger.info('Successfully fetched blog posts', context, { 
        total: result.total,
        page: result.page 
      });

      return {
        success: true,
        data: result,
      };

    } catch (error) {
      const appError = this.normalizeError(error, context);
      this.logger.error('Failed to fetch blog posts', context, { params }, appError);
      
      return {
        success: false,
        error: appError.message,
      };
    }
  }

  /**
   * Get blog post by ID
   */
  public async getById(id: string): Promise<ServiceResult<BlogPost>> {
    const context = 'getById';
    this.logger.debug('Fetching blog post by ID', context, { id });

    try {
      this.validateId(id);
      
      const cacheKey = this.getCacheKey('id', id);
      
      // Try cache first
      if (this.cache) {
        const cached = await this.cache.get<BlogPost>(cacheKey);
        if (cached.success && cached.data) {
          this.logger.debug('Returning cached blog post', context, { id });
          return cached;
        }
      }

      // Since we don't have a getById method, get all and filter
      const allPosts = await publicAPI.getPublishedPosts();
      const post = allPosts.find(p => p.id === id);

      if (!post) {
        throw new AppError(`Blog post with ID ${id} not found`, 'NOT_FOUND', 404);
      }

      const domainPost = BlogPostAdapter.toDomain(post);

      // Cache the result
      if (this.cache) {
        await this.cache.set(cacheKey, domainPost, this.cacheTTL);
      }

      this.logger.info('Successfully fetched blog post', context, { id, title: domainPost.title });

      return {
        success: true,
        data: domainPost,
      };

    } catch (error) {
      const appError = this.normalizeError(error, context);
      this.logger.error('Failed to fetch blog post by ID', context, { id }, appError);
      
      return {
        success: false,
        error: appError.message,
      };
    }
  }

  /**
   * Get published blog posts
   */
  public async getPublished(params?: Partial<PaginationParams>): Promise<ServiceResult<PaginatedResult<BlogPost>>> {
    // This is the same as getAll since we only get published posts
    return this.getAll(params);
  }

  /**
   * Get blog post by slug
   */
  public async getBySlug(slug: string): Promise<ServiceResult<BlogPost>> {
    const context = 'getBySlug';
    this.logger.debug('Fetching blog post by slug', context, { slug });

    try {
      this.validateSlug(slug);
      
      const cacheKey = this.getCacheKey('slug', slug);
      
      // Try cache first
      if (this.cache) {
        const cached = await this.cache.get<BlogPost>(cacheKey);
        if (cached.success && cached.data) {
          this.logger.debug('Returning cached blog post', context, { slug });
          return cached;
        }
      }

      // Get all posts and find by slug
      const allPosts = await publicAPI.getPublishedPosts();
      const post = allPosts.find(p => this.generateSlug(p.title) === slug);

      if (!post) {
        throw new AppError(`Blog post with slug ${slug} not found`, 'NOT_FOUND', 404);
      }

      const domainPost = BlogPostAdapter.toDomain(post);

      // Cache the result
      if (this.cache) {
        await this.cache.set(cacheKey, domainPost, this.cacheTTL);
      }

      this.logger.info('Successfully fetched blog post by slug', context, { 
        slug, 
        id: domainPost.id, 
        title: domainPost.title 
      });

      return {
        success: true,
        data: domainPost,
      };

    } catch (error) {
      const appError = this.normalizeError(error, context);
      this.logger.error('Failed to fetch blog post by slug', context, { slug }, appError);
      
      return {
        success: false,
        error: appError.message,
      };
    }
  }

  /**
   * Get posts by category
   */
  public async getByCategory(
    category: string, 
    params?: Partial<PaginationParams>
  ): Promise<ServiceResult<PaginatedResult<BlogPost>>> {
    const context = 'getByCategory';
    this.logger.debug('Fetching blog posts by category', context, { category, params });

    try {
      this.validateCategory(category);
      
      const cacheKey = this.getCacheKey('category', category, params);
      
      // Try cache first
      if (this.cache) {
        const cached = await this.cache.get<PaginatedResult<BlogPost>>(cacheKey);
        if (cached.success && cached.data) {
          this.logger.debug('Returning cached blog posts by category', context, { category });
          return cached;
        }
      }

      // Get all posts and filter by category
      const allPosts = await publicAPI.getPublishedPosts();
      const domainPosts = BlogPostAdapter.toDomainArray(allPosts);
      const filteredPosts = domainPosts.filter(post => 
        post.category?.toLowerCase() === category.toLowerCase()
      );

      const result = this.paginateResults(filteredPosts, params);

      // Cache the result
      if (this.cache) {
        await this.cache.set(cacheKey, result, this.cacheTTL);
      }

      this.logger.info('Successfully fetched blog posts by category', context, { 
        category,
        total: result.total,
        page: result.page 
      });

      return {
        success: true,
        data: result,
      };

    } catch (error) {
      const appError = this.normalizeError(error, context);
      this.logger.error('Failed to fetch blog posts by category', context, { category, params }, appError);
      
      return {
        success: false,
        error: appError.message,
      };
    }
  }

  /**
   * Get posts by date range
   */
  public async getByDateRange(
    startDate: Date, 
    endDate: Date, 
    params?: Partial<PaginationParams>
  ): Promise<ServiceResult<PaginatedResult<BlogPost>>> {
    const context = 'getByDateRange';
    this.logger.debug('Fetching blog posts by date range', context, { startDate, endDate, params });

    try {
      this.validateDateRange(startDate, endDate);
      
      const cacheKey = this.getCacheKey('dateRange', `${startDate.toISOString()}-${endDate.toISOString()}`, params);
      
      // Try cache first
      if (this.cache) {
        const cached = await this.cache.get<PaginatedResult<BlogPost>>(cacheKey);
        if (cached.success && cached.data) {
          this.logger.debug('Returning cached blog posts by date range', context, { startDate, endDate });
          return cached;
        }
      }

      // Get all posts and filter by date range
      const allPosts = await publicAPI.getPublishedPosts();
      const domainPosts = BlogPostAdapter.toDomainArray(allPosts);
      const filteredPosts = domainPosts.filter(post => {
        const postDate = new Date(post.publish_date || post.date);
        return postDate >= startDate && postDate <= endDate;
      });

      const result = this.paginateResults(filteredPosts, params);

      // Cache the result
      if (this.cache) {
        await this.cache.set(cacheKey, result, this.cacheTTL);
      }

      this.logger.info('Successfully fetched blog posts by date range', context, { 
        startDate,
        endDate,
        total: result.total,
        page: result.page 
      });

      return {
        success: true,
        data: result,
      };

    } catch (error) {
      const appError = this.normalizeError(error, context);
      this.logger.error('Failed to fetch blog posts by date range', context, { 
        startDate, 
        endDate, 
        params 
      }, appError);
      
      return {
        success: false,
        error: appError.message,
      };
    }
  }

  /**
   * Search blog posts
   */
  public async search(
    query: string, 
    params?: Partial<PaginationParams>
  ): Promise<ServiceResult<PaginatedResult<BlogPost>>> {
    const context = 'search';
    this.logger.debug('Searching blog posts', context, { query, params });

    try {
      this.validateSearchQuery(query);
      
      const cacheKey = this.getCacheKey('search', query, params);
      
      // Try cache first
      if (this.cache) {
        const cached = await this.cache.get<PaginatedResult<BlogPost>>(cacheKey);
        if (cached.success && cached.data) {
          this.logger.debug('Returning cached search results', context, { query });
          return cached;
        }
      }

      // Get all posts and filter by search query
      const allPosts = await publicAPI.getPublishedPosts();
      const domainPosts = BlogPostAdapter.toDomainArray(allPosts);
      const searchTerm = query.toLowerCase();
      const filteredPosts = domainPosts.filter(post => 
        post.title.toLowerCase().includes(searchTerm) ||
        post.content.toLowerCase().includes(searchTerm) ||
        post.excerpt?.toLowerCase().includes(searchTerm) ||
        post.category?.toLowerCase().includes(searchTerm)
      );

      const result = this.paginateResults(filteredPosts, params);

      // Cache the result
      if (this.cache) {
        await this.cache.set(cacheKey, result, this.cacheTTL);
      }

      this.logger.info('Successfully searched blog posts', context, { 
        query,
        total: result.total,
        page: result.page 
      });

      return {
        success: true,
        data: result,
      };

    } catch (error) {
      const appError = this.normalizeError(error, context);
      this.logger.error('Failed to search blog posts', context, { query, params }, appError);
      
      return {
        success: false,
        error: appError.message,
      };
    }
  }

  // CRUD operations (not implemented for read-only blog)
  public async create(): Promise<ServiceResult<BlogPost>> {
    throw new AppError('Blog post creation not supported in this implementation', 'NOT_SUPPORTED', 405);
  }

  public async update(): Promise<ServiceResult<BlogPost>> {
    throw new AppError('Blog post updates not supported in this implementation', 'NOT_SUPPORTED', 405);
  }

  public async delete(): Promise<ServiceResult<boolean>> {
    throw new AppError('Blog post deletion not supported in this implementation', 'NOT_SUPPORTED', 405);
  }

  public async exists(id: string): Promise<ServiceResult<boolean>> {
    try {
      const result = await this.getById(id);
      return {
        success: true,
        data: result.success,
      };
    } catch {
      return {
        success: true,
        data: false,
      };
    }
  }

  // Private helper methods
  private paginateResults(items: BlogPost[], params?: Partial<PaginationParams>): PaginatedResult<BlogPost> {
    const page = params?.page || 1;
    const limit = Math.min(params?.limit || 10, 100);
    const offset = (page - 1) * limit;
    
    const total = items.length;
    const totalPages = Math.ceil(total / limit);
    const paginatedItems = items.slice(offset, offset + limit);

    return {
      items: paginatedItems,
      total,
      page,
      limit,
      totalPages,
      hasNext: page < totalPages,
      hasPrev: page > 1,
    };
  }

  private getCacheKey(type: string, identifier?: string | Partial<PaginationParams>, params?: Partial<PaginationParams>): string {
    const parts = [this.cachePrefix, type];
    
    if (identifier) {
      if (typeof identifier === 'string') {
        parts.push(identifier);
      } else {
        parts.push(JSON.stringify(identifier));
      }
    }
    
    if (params) {
      parts.push(JSON.stringify(params));
    }
    
    return parts.join(':');
  }

  private generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  private normalizeError(error: unknown, context: string): AppError {
    if (error instanceof AppError) {
      return error;
    }

    if (error instanceof Error) {
      return new AppError(
        `Error in ${context}: ${error.message}`,
        'BLOG_SERVICE_ERROR',
        500
      );
    }

    return new AppError(
      `Unknown error in ${context}: ${String(error)}`,
      'UNKNOWN_ERROR',
      500
    );
  }

  // Validation methods
  private validateId(id: string): void {
    if (!id || typeof id !== 'string' || id.trim().length === 0) {
      throw new ValidationError('Invalid blog post ID', 'id');
    }
  }

  private validateSlug(slug: string): void {
    if (!slug || typeof slug !== 'string' || slug.trim().length === 0) {
      throw new ValidationError('Invalid blog post slug', 'slug');
    }
  }

  private validateCategory(category: string): void {
    if (!category || typeof category !== 'string' || category.trim().length === 0) {
      throw new ValidationError('Invalid category', 'category');
    }
  }

  private validateDateRange(startDate: Date, endDate: Date): void {
    if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
      throw new ValidationError('Invalid date range: dates must be Date objects');
    }

    if (startDate > endDate) {
      throw new ValidationError('Invalid date range: start date must be before end date');
    }
  }

  private validateSearchQuery(query: string): void {
    if (!query || typeof query !== 'string' || query.trim().length === 0) {
      throw new ValidationError('Invalid search query', 'query');
    }

    if (query.trim().length < 2) {
      throw new ValidationError('Search query must be at least 2 characters long', 'query');
    }
  }
} 